import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Heart, User, LogOut, Shield, Menu, X } from 'lucide-react';
import AuthModal from './AuthModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/properties', label: 'Properties', testId: 'properties-nav-link' },
    { to: '/calendar', label: 'Auction Calendar', testId: 'calendar-nav-link' },
    { to: '/auction-dates', label: 'Auction Dates', testId: 'auction-dates-nav-link' },
    { to: '/finance', label: 'Finance', testId: 'finance-nav-link' },
  ];

  return (
    <>
      <header 
        className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60"
        data-testid="main-header"
      >
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex h-16 sm:h-20 items-center justify-between gap-2 sm:gap-4">
            <Link 
              to="/" 
              className="flex items-center gap-0.5 flex-shrink-0"
              data-testid="logo-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_live-listings-1/artifacts/kd4qptzr_Vitthal%20Watermark%20Image.png" 
                alt="AuctionGuru Logo" 
                className="h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 object-contain"
              />
              <span className="font-heading font-bold text-lg sm:text-xl md:text-2xl tracking-tight whitespace-nowrap">
                <span className="text-slate-900">Auction</span><span className="text-[#F5A623]">Guru</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-4 lg:space-x-6 xl:space-x-8 flex-1 justify-center">
              {navLinks.map((link) => (
                <Link 
                  key={link.to}
                  to={link.to} 
                  className={`text-sm font-medium transition-colors hover:text-[#F5A623] whitespace-nowrap ${isActive(link.to) ? 'text-[#F5A623]' : 'text-slate-700'}`}
                  data-testid={link.testId}
                >
                  {link.label}
                </Link>
              ))}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className={`text-sm font-medium transition-colors hover:text-[#F5A623] flex items-center gap-1 whitespace-nowrap ${isActive('/admin') ? 'text-[#F5A623]' : 'text-slate-700'}`}
                  data-testid="admin-nav-link"
                >
                  <Shield className="h-4 w-4" />
                  Admin
                </Link>
              )}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {isAuthenticated ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/dashboard/saved')}
                    data-testid="saved-properties-btn"
                    className="h-8 w-8 sm:h-10 sm:w-10"
                  >
                    <Heart className="h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid="user-menu-btn" className="h-8 w-8 sm:h-10 sm:w-10">
                        <User className="h-4 w-4 sm:h-5 sm:w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <div className="px-2 py-2">
                        <p className="text-sm font-medium">{user?.email}</p>
                        <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/dashboard/saved')} data-testid="dashboard-menu-item">
                        <Heart className="mr-2 h-4 w-4" />
                        Saved Properties
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/dashboard/searches')} data-testid="searches-menu-item">
                        Saved Searches
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/dashboard/alerts')} data-testid="alerts-menu-item">
                        Alert Subscriptions
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">
                            <Shield className="mr-2 h-4 w-4" />
                            Admin Dashboard
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button 
                  onClick={() => setShowAuthModal(true)} 
                  className="btn-primary text-xs sm:text-sm px-3 sm:px-4 h-8 sm:h-10"
                  data-testid="login-btn"
                >
                  Login / Sign Up
                </Button>
              )}

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-8 w-8 sm:h-10 sm:w-10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-white">
            <nav className="container mx-auto px-4 py-4 flex flex-col space-y-3">
              {navLinks.map((link) => (
                <Link 
                  key={link.to}
                  to={link.to} 
                  className={`text-sm font-medium py-2 px-3 rounded-lg transition-colors ${isActive(link.to) ? 'text-[#F5A623] bg-[#F5A623]/10' : 'text-slate-700 hover:bg-slate-100'}`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-${link.testId}`}
                >
                  {link.label}
                </Link>
              ))}
              {isAdmin && (
                <Link 
                  to="/admin" 
                  className={`text-sm font-medium py-2 px-3 rounded-lg transition-colors flex items-center gap-2 ${isActive('/admin') ? 'text-[#F5A623] bg-[#F5A623]/10' : 'text-slate-700 hover:bg-slate-100'}`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-admin-nav-link"
                >
                  <Shield className="h-4 w-4" />
                  Admin
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      <AuthModal 
        open={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </>
  );
};

export default Header;
